// P96 (Tudor 2026-10-28/29) — CP Marketing → Newsletters, super admins only: a newsletter's subscribers, the accounts
// (their flag and consent audit) and the site box's guests (an address without an account, subscribed from the region
// box, no double opt-in), with the counters, the filters, a subscriber's consent proof, the CSV export of the
// filter's subscribed rows (each with its unsubscribe link, logged) and two actions on a guest: unsubscribe, delete.
// The canvas: claude.ai artifact „P96 Marketing Leads”, boards Newsletters 1440 / 2048. The state lives in the address.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link, withRouter } from "react-router-dom"
import { Button, Card, CardBody, Container, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import { deleteGuest, getExport, getSubscribers, getSummary, unsubscribeGuest } from "./api"
import {
  LISTS, PAGES, REGIONS, SOURCES, SOURCE_FILTERS, SPORTS, STATUSES, TYPES, apiFilters, errorText, formatCount, formatDate,
  formatDateTime, readNewsletterQuery, regionLabel, writeNewsletterQuery,
} from "./newsletterLabels"
import "../../../assets/scss/custom/pages/_marketingNewsletters.scss"

export const NEWSLETTERS_PATH = "/marketing/newsletters"
const LIMIT = 25

const Badge = ({ tone, children }) => <span className={`badge badge-soft-${tone} font-size-12`}>{children}</span>
const typeBadge = row => (row.type === "guest" ? <Badge tone="info">Email only</Badge> : <Badge tone="primary">Account</Badge>)
const sourceBadge = row => { const [label, tone] = SOURCES[row.source] || [row.source, "light"]; return <Badge tone={tone}>{label}</Badge> }
const statusBadge = row => (row.status === "subscribed" ? <Badge tone="success">Subscribed</Badge> : <Badge tone="secondary">Unsubscribed</Badge>)
const nameOf = row => [row.firstName, row.lastName].filter(Boolean).join(" ")

function SubscriberDialog({ row, onClose, onChanged }) {
  const [confirm, setConfirm] = useState(null)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState("")
  const guest = row.type === "guest"
  const act = action => {
    setPending(true); setError("")
    ;(action === "delete" ? deleteGuest(row.id) : unsubscribeGuest(row.id))
      .then(() => onChanged(action === "delete" ? "The subscriber was deleted." : "The subscriber was unsubscribed."))
      .catch(failure => { setPending(false); setConfirm(null); setError(errorText(failure, "The action didn't go through. Try again.")) })
  }
  const consent = row.consent || {}
  const utm = Object.entries(consent.utm || {}).map(([key, value]) => `${key}: ${value}`).join(" · ")
  return <Modal isOpen toggle={pending ? undefined : onClose} className="newsletters-drawer" aria-labelledby="newsletters-dialog-title"
    backdrop={pending ? "static" : true} keyboard={!pending}>
    <ModalHeader toggle={pending ? undefined : onClose} id="newsletters-dialog-title">
      {guest ? row.email : nameOf(row) || row.email}
      <div className="newsletters-badges">{typeBadge(row)} {statusBadge(row)}</div>
    </ModalHeader>
    <ModalBody>
      <section className="newsletters-section" aria-label="Consent">
        <div className="newsletters-section-label">Consent</div>
        <dl className="newsletters-facts">
          {guest ? <>
            <dt>Given</dt><dd>{formatDateTime(consent.givenAt)} · {(consent.language || row.language || "").toUpperCase()} · {PAGES[consent.page] || consent.page || "—"}</dd>
            <dt>Welcome email</dt><dd>sent at the subscription, with the unsubscribe link</dd>
            <dt>Campaign</dt><dd>{utm || "—"}</dd>
            <dt>Unsubscribe link</dt><dd>personal, in every email</dd>
          </> : <>
            <dt>Source</dt><dd>{sourceBadge(row)}</dd>
            <dt>Since</dt><dd>{row.source === "unknown" ? "before the consent audit (no date kept)" : formatDateTime(row.subscribedAt || row.unsubscribedAt)}</dd>
          </>}
          {row.status === "unsubscribed" && <><dt>Unsubscribed</dt><dd>{formatDateTime(row.unsubscribedAt)}</dd></>}
        </dl>
        {guest && consent.text && <>
          <div className="text-muted font-size-12 mt-2">Text shown ({consent.version}):</div>
          <blockquote className="newsletters-quote">{consent.text}</blockquote>
        </>}
      </section>
      <section className="newsletters-section" aria-label="Account">
        <div className="newsletters-section-label">Account</div>
        {guest ? <p className="mb-0">No account with this email yet. If one is created and activated, the subscription moves to it.</p>
          : <p className="mb-0"><Link to={`/accounts/players/${row.accountId}`}>#{row.userId} · {nameOf(row) || row.email}</Link> — its newsletter settings are changed on the account.</p>}
      </section>
      {error && <p role="alert" className="text-danger mb-0">{error}</p>}
    </ModalBody>
    {guest ? <ModalFooter className="newsletters-actions">
      {confirm ? <>
        <span className="mr-auto">{confirm === "delete" ? "Delete this subscriber and its consent proof? This can't be undone." : "Unsubscribe this address from the newsletter?"}</span>
        <Button color="light" disabled={pending} onClick={() => setConfirm(null)}>Cancel</Button>
        <Button color={confirm === "delete" ? "danger" : "warning"} disabled={pending} onClick={() => act(confirm)}>
          {pending ? <Spinner size="sm" /> : confirm === "delete" ? "Delete" : "Unsubscribe"}
        </Button>
      </> : <>
        <span className="mr-auto text-muted font-size-12">Both ask for confirmation and are written to the activity log.</span>
        {row.status === "subscribed" && <Button outline color="warning" onClick={() => setConfirm("unsubscribe")}>Unsubscribe</Button>}
        <Button outline color="danger" onClick={() => setConfirm("delete")}>Delete subscriber</Button>
      </>}
    </ModalFooter> : null}
  </Modal>
}

export function MarketingNewsletters({ history, location }) {
  const state = useMemo(() => readNewsletterQuery(location.search), [location.search])
  const [searchText, setSearchText] = useState(state.q)
  const [list, setList] = useState({ status: "loading" })
  const [summary, setSummary] = useState({ status: "loading" })
  const [open, setOpen] = useState(null)
  const [notice, setNotice] = useState("")
  const [exporting, setExporting] = useState({ busy: false, error: "" })
  const timer = useRef()
  const request = useRef(0)
  const update = useCallback(next => {
    const search = writeNewsletterQuery(next)
    if (search !== history.location.search) history.replace({ ...history.location, search })
  }, [history])

  const loadList = useCallback(() => {
    const id = ++request.current
    setList({ status: "loading" })
    getSubscribers(apiFilters(state, LIMIT))
      .then(data => request.current === id && setList({ status: "ready", data }))
      .catch(error => request.current === id && setList({ status: "error", message: errorText(error, "The subscribers didn't load. Click Retry.") }))
  }, [state])
  const loadSummary = useCallback(() => {
    setSummary({ status: "loading" })
    getSummary(state.list)
      .then(data => setSummary({ status: "ready", data }))
      .catch(error => setSummary({ status: "error", message: errorText(error, "The counters didn't load.") }))
  }, [state.list])
  useEffect(() => {
    loadList()
    return () => { request.current += 1 }
  }, [loadList])
  useEffect(loadSummary, [loadSummary])
  useEffect(() => { setSearchText(state.q) }, [state.q])
  useEffect(() => () => clearTimeout(timer.current), [])

  const set = changes => { clearTimeout(timer.current); update({ ...state, q: searchText, ...changes, page: changes.page || 1 }) }
  const changeSearch = event => {
    const value = event.target.value
    setSearchText(value)
    clearTimeout(timer.current)
    timer.current = setTimeout(() => update({ ...readNewsletterQuery(history.location.search), q: value, page: 1 }), 400)
  }
  const changed = message => { setOpen(null); setNotice(message); loadList(); loadSummary() }
  const downloadExport = async () => {
    setExporting({ busy: true, error: "" })
    let url, link
    try {
      const data = await getExport(apiFilters(state, LIMIT))
      url = URL.createObjectURL(new Blob([data], { type: "text/csv;charset=utf-8" }))
      link = document.createElement("a")
      link.href = url
      link.download = `newsletter-subscribers-${state.list}-${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(link); link.click()
      setExporting({ busy: false, error: "" })
    } catch (error) {
      setExporting({ busy: false, error: errorText(error, "The CSV could not be generated. Please retry.") })
    } finally { link?.remove(); if (url) URL.revokeObjectURL(url) }
  }

  const counts = summary.data
  const kpis = [
    ["Subscribed", counts?.subscribed.total, counts && `${formatCount(counts.subscribed.accounts)} accounts · ${formatCount(counts.subscribed.guests)} email only`],
    ["New, last 30 days", counts?.newLast30Days.total, counts && `${formatCount(counts.newLast30Days.accounts)} accounts · ${formatCount(counts.newLast30Days.guests)} from the site's forms`],
    ["Email only", counts?.subscribed.guests, "from the site's forms · no account yet"],
    ["Unsubscribed, last 30 days", counts?.unsubscribedLast30Days.total, counts && `${formatCount(counts.unsubscribedLast30Days.accounts)} accounts · ${formatCount(counts.unsubscribedLast30Days.guests)} email only`],
  ]
  const typeCount = key => (counts ? { all: counts.subscribed.total, account: counts.subscribed.accounts, guest: counts.subscribed.guests }[key] : undefined)
  const rows = list.data?.items || []
  const total = list.data?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / LIMIT))
  const first = rows.length ? (state.page - 1) * LIMIT + 1 : 0
  const subscribedView = state.status === "subscribed"

  return <div className="page-content marketing-newsletters"><Container fluid>
    <Breadcrumbs title="Marketing" breadcrumbItem="Newsletters" path={NEWSLETTERS_PATH} />
    <ul className="nav nav-tabs nav-tabs-custom mb-3" role="tablist" aria-label="Newsletter">
      {LISTS.map(([key, label]) => <li key={key} className="nav-item">
        <button type="button" role="tab" aria-selected={state.list === key} className={`nav-link${state.list === key ? " active" : ""}`}
          onClick={() => set({ list: key })}>{label}</button>
      </li>)}
    </ul>

    {summary.status === "error" ? <p role="alert" className="text-danger">{summary.message} <button type="button" className="btn btn-link btn-sm p-0" onClick={loadSummary}>Retry</button></p>
      : <div className="newsletters-kpis" aria-busy={summary.status === "loading"}>
        {kpis.map(([label, value, meta]) => <Card key={label} className="mb-0"><CardBody>
          <div className="text-muted">{label}</div>
          <div className="newsletters-kpi-value">{formatCount(value)}</div>
          <div className="text-muted font-size-12">{meta || " "}</div>
        </CardBody></Card>)}
      </div>}

    <Card><CardBody className="p-0">
      <div className="p-3 border-bottom">
        <div className="d-flex flex-wrap justify-content-between align-items-start newsletters-toolbar">
          <div className="newsletters-chips" role="group" aria-label="Subscriber type">
            {TYPES.map(([key, label]) => <button key={key} type="button" aria-pressed={state.type === key}
              className={`newsletters-chip${state.type === key ? " active" : ""}`} onClick={() => set({ type: key })}>
              {label} <span className="newsletters-chip-count">{formatCount(typeCount(key))}</span>
            </button>)}
          </div>
          <div className="text-right">
            <Button color="primary" disabled={exporting.busy} onClick={downloadExport}>
              <i className="bx bx-download mr-1" aria-hidden="true" />{exporting.busy ? "Generating CSV…" : "Export CSV"}
            </Button>
            <div className="text-muted font-size-12 mt-1">Subscribed rows of this filter, with the unsubscribe link · logged in the activity log</div>
          </div>
        </div>
        {exporting.error && <div role="alert" className="alert alert-danger mt-2 mb-0">{exporting.error}</div>}
        <div className="newsletters-filters mt-3">
          <div className="search-box newsletters-search"><div className="position-relative">
            <input className="form-control" aria-label="Search a name or an email" placeholder="Search a name or an email (3 characters at least)"
              value={searchText} maxLength={100} onChange={changeSearch} />
            <i className="bx bx-search-alt search-icon" aria-hidden="true" />
          </div></div>
          <select className="custom-select" aria-label="Sport" value={state.sport} onChange={event => set({ sport: event.target.value })}>
            {SPORTS.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
          <select className="custom-select" aria-label="Region" value={state.regionId} onChange={event => set({ regionId: event.target.value })}>
            {REGIONS.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
          <select className="custom-select" aria-label="Source" value={state.source} disabled={!subscribedView} onChange={event => set({ source: event.target.value })}>
            {SOURCE_FILTERS.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
          <select className="custom-select" aria-label="Status" value={state.status} onChange={event => set({ status: event.target.value })}>
            {STATUSES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
          <label className="newsletters-date">{subscribedView ? "Subscribed from" : "Unsubscribed from"}
            <input type="date" className="form-control" value={state.from} onChange={event => set({ from: event.target.value })} />
          </label>
          <label className="newsletters-date">to
            <input type="date" className="form-control" value={state.to} onChange={event => set({ to: event.target.value })} />
          </label>
          <button type="button" className="btn btn-link" onClick={() => { setSearchText(""); update({ list: state.list, type: "all", status: "subscribed", sport: "", regionId: "", source: "", from: "", to: "", q: "", page: 1 }) }}>Reset filters</button>
        </div>
      </div>
      {notice && <div role="status" className="alert alert-success m-3">{notice}</div>}
      {list.status === "error" ? <div className="newsletters-state" role="alert"><p>{list.message}</p>
        <button type="button" className="btn btn-primary" onClick={loadList}>Retry</button></div>
        : list.status === "loading" ? <div className="newsletters-state" role="status"><Spinner size="sm" className="mr-2" />Loading the subscribers…</div>
          : !rows.length ? <div className="newsletters-state" role="status"><p className="mb-0">No subscriber matches these filters.</p></div>
            : <div className="table-responsive"><table className="table table-hover newsletters-table" aria-label="Newsletter subscribers">
              <thead className="thead-light"><tr>
                <th scope="col">Subscriber</th><th scope="col">Type</th><th scope="col">Sport</th><th scope="col">City · Region</th>
                <th scope="col">Lang</th><th scope="col">Source</th><th scope="col">{subscribedView ? "Subscribed" : "Unsubscribed"}</th>
                <th scope="col">Status</th>
              </tr></thead>
              <tbody>{rows.map(row => <tr key={`${row.type}-${row.id}`}>
                <td><button type="button" className="btn btn-link p-0 text-left font-weight-medium" onClick={() => { setNotice(""); setOpen(row) }}>
                  {row.type === "guest" ? row.email : nameOf(row) || row.email}</button>
                  <div className="text-muted font-size-12">{row.type === "guest" ? "no account" : `${row.email} · #${row.userId}`}</div></td>
                <td data-label="Type">{typeBadge(row)}</td>
                <td data-label="Sport">{row.sportType ? row.sportType[0].toUpperCase() + row.sportType.slice(1) : "—"}</td>
                <td data-label="City · Region">{row.city || "—"}<div className="text-muted font-size-12">{regionLabel(row.regionId)}</div></td>
                <td data-label="Lang">{(row.language || "—").toUpperCase()}</td>
                <td data-label="Source">{sourceBadge(row)}</td>
                <td data-label={subscribedView ? "Subscribed" : "Unsubscribed"}>{formatDate(subscribedView ? row.subscribedAt : row.unsubscribedAt)}</td>
                <td data-label="Status">{statusBadge(row)}</td>
              </tr>)}</tbody>
            </table></div>}
      {list.status === "ready" && rows.length > 0 && <div className="newsletters-footer">
        <div role="status">Showing <b>{first}–{first + rows.length - 1}</b> of <b>{formatCount(total)}</b></div>
        <nav aria-label="Subscriber pages" className="newsletters-pager">
          <button type="button" aria-label="Previous page" className="btn btn-light btn-sm" disabled={state.page <= 1}
            onClick={() => set({ page: state.page - 1 })}><i className="bx bx-chevron-left" aria-hidden="true" /></button>
          <span>Page {state.page} of {pages}</span>
          <button type="button" aria-label="Next page" className="btn btn-light btn-sm" disabled={state.page >= pages}
            onClick={() => set({ page: state.page + 1 })}><i className="bx bx-chevron-right" aria-hidden="true" /></button>
        </nav>
      </div>}
    </CardBody></Card>
    {open && <SubscriberDialog row={open} onClose={() => setOpen(null)} onChanged={changed} />}
  </Container></div>
}

export default withRouter(MarketingNewsletters)
